import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { ProfileMenuComponent } from '../profile/profile-menu';
import { BottomBarComponent } from './bottom-bar/bottom-bar';
import {APP_ROLES, AppRole} from '../security/roles';
import { KeycloakService } from '../security/keycloak.service';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { AlertNotification } from '../models/alert-notification.model';
import { AlertNotificationService } from '../services/alert-notification.service';

type MenuItem = { label: string; path: string; roles: AppRole[] };

@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, ProfileMenuComponent, BottomBarComponent, DatePipe],
  templateUrl: './layout.html',
  styleUrl: './layout.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
  appName = 'VIGIMARE';
  APP_ROLES = APP_ROLES;
  notificationsOpen = signal(false);
  notifications = signal<AlertNotification[]>([]);
  unreadCount = signal(0);
  notificationsLoading = signal(false);
  notificationsError = signal<string | null>(null);

  private notificationSubscription?: Subscription;
  private reconnectAttempts = 0;
  private reconnectTimer?: ReturnType<typeof setTimeout>;

  constructor(
    public keycloakService: KeycloakService,
    private notificationService: AlertNotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadNotifications();
    this.loadUnreadCount();
    this.listenForNotifications();
  }

  ngOnDestroy(): void {
    this.notificationSubscription?.unsubscribe();
    clearTimeout(this.reconnectTimer);
  }

  canShow(roles: AppRole[]): boolean {
    return this.keycloakService.isAdmin() || this.keycloakService.hasAnyRole(roles);
  }

  openExternalPlatform(): void {
    window.open('https://vigimare.diglt.de/', '_blank', 'noopener,noreferrer');
  }

  toggleNotifications(): void {
    this.notificationsOpen.update(open => !open);

    if (this.notificationsOpen()) {
      this.loadNotifications();
    }
  }

  closeNotifications(): void {
    this.notificationsOpen.set(false);
  }

  markAsRead(notification: AlertNotification): void {
    const target = this.resolveNotificationRoute(notification);

    if (notification.read) {
      this.router.navigate([target], { queryParams: { alertId: notification.alertId } });
      this.closeNotifications();
      return;
    }

    this.notificationService.markAsRead(notification.id).subscribe({
      next: (updated) => {
        this.notifications.update(current =>
          current.map(item => item.id === updated.id ? updated : item)
        );
        this.unreadCount.update(count => Math.max(0, count - 1));
        this.router.navigate([target], { queryParams: { alertId: notification.alertId } });
        this.closeNotifications();
      },
      error: () => this.notificationsError.set('Failed to mark notification as read.')
    });
  }

  private resolveNotificationRoute(notification: AlertNotification): string {
    if (
      notification.type === 'ASSIGNED_TO_USER' ||
      notification.type === 'ASSIGNED_TO_ORGANIZATION' ||
      notification.type === 'COMMENT_ON_ASSIGNED'
    ) {
      return '/alerts';
    }
    return '/incidents';
  }

  markAllNotificationsAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notifications.update(current => current.map(item => ({ ...item, read: true })));
        this.unreadCount.set(0);
      },
      error: () => this.notificationsError.set('Failed to mark notifications as read.')
    });
  }

  private loadNotifications(): void {
    this.notificationsLoading.set(true);
    this.notificationsError.set(null);

    this.notificationService.getAll().subscribe({
      next: (notifications) => {
        this.notifications.set(notifications);
        this.notificationsLoading.set(false);
      },
      error: () => {
        this.notificationsError.set('Failed to load notifications.');
        this.notificationsLoading.set(false);
      }
    });
  }

  private loadUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe({
      next: ({ count }) => this.unreadCount.set(count),
      error: () => this.unreadCount.set(0)
    });
  }

  private listenForNotifications(): void {
    this.notificationSubscription = this.notificationService.listenForNotifications().subscribe({
      next: ({ notification, unreadCount }) => {
        this.reconnectAttempts = 0;
        this.notifications.update(current => [notification, ...current.filter(item => item.id !== notification.id)]);
        this.unreadCount.set(unreadCount);
      },
      error: () => {
        this.notificationSubscription?.unsubscribe();
        this.notificationSubscription = undefined;
        const delay = Math.min(5000 * Math.pow(2, this.reconnectAttempts), 30000);
        this.reconnectAttempts++;
        this.reconnectTimer = setTimeout(() => this.listenForNotifications(), delay);
      }
    });
  }

  menu: MenuItem[] = [
    { label: 'Home',
      path: '/',
      roles: [
        APP_ROLES.ADMIN,
        APP_ROLES.OPERATOR,
        APP_ROLES.ANALYST,
        APP_ROLES.VIEWER
      ]
    },
    // { label: 'Dashboard', path: '/dashboard' },
    { label: 'Operational Overview',
      path: '/overview',
      roles: [
        APP_ROLES.ADMIN,
        APP_ROLES.OPERATOR,
        APP_ROLES.ANALYST,
        APP_ROLES.VIEWER
      ]
    },
    { label: 'Alert & Threat Monitoring',
      path: '/alerts',
      roles: [
        APP_ROLES.ADMIN,
        APP_ROLES.OPERATOR,
        APP_ROLES.ANALYST
      ]
    },
    // { label: 'Maritime Activity', path: '/maritime' },
    { label: 'Cyber & System Health',
      path: '/cyber',
      roles: [
        APP_ROLES.ADMIN,
        APP_ROLES.OPERATOR,
        APP_ROLES.ANALYST
      ]
    },
    { label: 'Incident Management',
      path: '/incidents',
      roles: [
        APP_ROLES.ADMIN,
        APP_ROLES.OPERATOR,
        APP_ROLES.ANALYST
      ]
    },
    { label: 'Administration',
      path: '/administration',
      roles: [
        APP_ROLES.ADMIN
      ]
    }
  ];
}
export class TopbarComponent {}
